import {defineMessages} from 'react-intl'

/**
 * Catálogo de Campañas (`/campaigns`). Namespace raíz de la Fase 1.9: `comms.*`.
 *
 * Glosario: **target → "destinatario"** (el contacto al que la campaña le va a
 * enviar). Se usa la misma palabra que el módulo `/recipients` a propósito: son
 * la misma persona vista a dos niveles — la lista maestra y los de una campaña
 * concreta —, y así la columna "Recipient" del registro concuerda con la
 * pestaña "Targets". El EN mantiene *target*.
 *
 * No se traducen: los enums de estado servidos por el backend (`campaign.status`,
 * `target.state`, `log.status`, `scheduleType`), los nombres de canal
 * (SMS/Email/WhatsApp/Telegram) ni los errores del backend.
 */
export const CAMPAIGN_MESSAGES = defineMessages({
  // ── Navegación del módulo ──
  navCampaigns: {id: 'MENU.item.campaigns', defaultMessage: 'Campaigns'},
  navTemplates: {id: 'MENU.item.templates', defaultMessage: 'Templates'},
  navAutomations: {id: 'MENU.item.automations', defaultMessage: 'Automations'},
  navRecipients: {id: 'MENU.item.recipients', defaultMessage: 'Recipients'},
  navDialer: {id: 'comms.campaigns.nav.dialer', defaultMessage: 'PolicyDial Dialer'},

  // ── Listado ──
  pageTitle: {id: 'comms.campaigns.page.title', defaultMessage: 'Messaging campaigns'},
  pageSubtitle: {
    id: 'comms.campaigns.page.subtitle',
    defaultMessage: 'Coordinate SMS, email, WhatsApp, and Telegram flows from one workspace.',
  },
  newCampaign: {id: 'comms.campaigns.page.new', defaultMessage: 'New campaign'},
  loadError: {
    id: 'comms.campaigns.page.loadError',
    defaultMessage: 'Unable to load campaigns: {message}',
  },
  searchPlaceholder: {
    id: 'comms.campaigns.page.searchPlaceholder',
    defaultMessage: 'Search campaigns…',
  },
  allStatuses: {id: 'comms.campaigns.page.allStatuses', defaultMessage: 'All'},
  noMatch: {id: 'comms.campaigns.page.noMatch', defaultMessage: 'No campaigns match your filters.'},
  listEmpty: {
    id: 'comms.campaigns.page.empty',
    defaultMessage: 'No campaigns yet. Create your first one!',
  },
  noDescription: {id: 'comms.campaigns.noDescription', defaultMessage: 'No description'},
  deliveryRate: {id: 'comms.campaigns.card.deliveryRate', defaultMessage: 'Delivery rate'},
  lastRunShort: {id: 'comms.campaigns.card.lastRun', defaultMessage: 'Last run: {date}'},

  // ── Métricas ──
  statTargets: {id: 'comms.campaigns.stat.targets', defaultMessage: 'Targets'},
  statDelivered: {id: 'comms.campaigns.stat.delivered', defaultMessage: 'Delivered'},
  statFailed: {id: 'comms.campaigns.stat.failed', defaultMessage: 'Failed'},

  // ── Detalle: cabecera ──
  detailLoadError: {
    id: 'comms.campaigns.detail.loadError',
    defaultMessage: 'Unable to load campaign: {message}',
  },
  notFound: {id: 'comms.campaigns.detail.notFound', defaultMessage: 'Campaign not found'},
  autoRefresh: {id: 'comms.campaigns.detail.autoRefresh', defaultMessage: 'Auto-refresh'},
  refreshData: {id: 'comms.campaigns.detail.refreshData', defaultMessage: 'Refresh data'},
  editCampaign: {id: 'comms.campaigns.detail.editCampaign', defaultMessage: 'Edit campaign'},
  addTarget: {id: 'comms.campaigns.detail.addTarget', defaultMessage: 'Add target'},
  stopRecurring: {id: 'comms.campaigns.detail.stopRecurring', defaultMessage: 'Stop recurring'},
  pause: {id: 'comms.campaigns.detail.pause', defaultMessage: 'Pause'},
  requestApproval: {
    id: 'comms.campaigns.detail.requestApproval',
    defaultMessage: 'Request Approval',
  },
  launch: {id: 'comms.campaigns.detail.launch', defaultMessage: 'Launch'},
  tooltipRequest: {
    id: 'comms.campaigns.detail.tooltipRequest',
    defaultMessage: 'Add at least one target before requesting approval.',
  },
  tooltipLaunch: {
    id: 'comms.campaigns.detail.tooltipLaunch',
    defaultMessage: 'Add at least one target before launching.',
  },
  metaSchedule: {
    id: 'comms.campaigns.detail.metaSchedule',
    defaultMessage: '<b>Schedule:</b> {value}',
  },
  metaNextRun: {
    id: 'comms.campaigns.detail.metaNextRun',
    defaultMessage: '<b>Next run:</b> {value}',
  },
  metaLastRun: {
    id: 'comms.campaigns.detail.metaLastRun',
    defaultMessage: '<b>Last run:</b> {value}',
  },
  notSet: {id: 'comms.campaigns.detail.notSet', defaultMessage: 'Not set'},

  // ── Detalle: pestañas y tablas ──
  activityLog: {id: 'comms.campaigns.detail.activityLog', defaultMessage: 'Activity log'},
  removeAllTargets: {
    id: 'comms.campaigns.detail.removeAllTargets',
    defaultMessage: 'Remove all targets',
  },
  clearAll: {id: 'comms.campaigns.detail.clearAll', defaultMessage: 'Clear all'},
  loadingTargets: {id: 'comms.campaigns.detail.loadingTargets', defaultMessage: 'Loading targets'},
  noTargets: {id: 'comms.campaigns.detail.noTargets', defaultMessage: 'No targets added yet'},
  loadingLogs: {id: 'comms.campaigns.detail.loadingLogs', defaultMessage: 'Loading logs'},
  noLogs: {
    id: 'comms.campaigns.detail.noLogs',
    defaultMessage: 'No logs found for this campaign',
  },
  colLastChannel: {id: 'comms.campaigns.detail.colLastChannel', defaultMessage: 'Last channel'},
  colErrors: {id: 'comms.campaigns.detail.colErrors', defaultMessage: 'Errors'},
  colTimestamp: {id: 'comms.campaigns.detail.colTimestamp', defaultMessage: 'Timestamp'},
  colProviderId: {id: 'comms.campaigns.detail.colProviderId', defaultMessage: 'Provider ID'},
  recipient: {id: 'comms.campaigns.detail.recipient', defaultMessage: 'Recipient'},
  showingRange: {
    id: 'comms.campaigns.detail.showingRange',
    defaultMessage: 'Showing {from}–{to} of {total}',
  },

  // ── Detalle: panel derecho ──
  collapsePanel: {id: 'comms.campaigns.detail.collapsePanel', defaultMessage: 'Collapse panel'},
  expandPanel: {id: 'comms.campaigns.detail.expandPanel', defaultMessage: 'Expand panel'},
  subjectLine: {
    id: 'comms.campaigns.detail.subjectLine',
    defaultMessage: '<b>Subject:</b> {subject}',
  },
  noTemplateAssigned: {
    id: 'comms.campaigns.detail.noTemplateAssigned',
    defaultMessage: 'No template assigned. Add one before launching.',
  },
  sendTestTitle: {id: 'comms.campaigns.detail.sendTestTitle', defaultMessage: 'Send test message'},
  recipientPlaceholder: {
    id: 'comms.campaigns.detail.recipientPlaceholder',
    defaultMessage: 'Email or phone…',
  },
  advancedOptions: {
    id: 'comms.campaigns.detail.advancedOptions',
    defaultMessage: 'Advanced Options',
  },
  fromName: {id: 'comms.campaigns.detail.fromName', defaultMessage: 'From Name'},
  fromNamePlaceholder: {
    id: 'comms.campaigns.detail.fromNamePlaceholder',
    defaultMessage: 'Marketing Team',
  },
  fromEmail: {id: 'comms.campaigns.detail.fromEmail', defaultMessage: 'From Email'},
  replyTo: {id: 'comms.campaigns.detail.replyTo', defaultMessage: 'Reply-To'},
  includeLogo: {
    id: 'comms.campaigns.detail.includeLogo',
    defaultMessage: 'Include Company Logo',
  },
  sendTest: {id: 'comms.campaigns.detail.sendTest', defaultMessage: 'Send test'},
  previewTitle: {id: 'comms.campaigns.detail.previewTitle', defaultMessage: 'Preview — {channel}'},

  // ── Detalle: modales de confirmación ──
  stopModalTitle: {
    id: 'comms.campaigns.stop.title',
    defaultMessage: 'Stop Recurring Campaign',
  },
  stopModalConfirm: {id: 'comms.campaigns.stop.confirm', defaultMessage: 'Stop future runs?'},
  stopModalBody: {
    id: 'comms.campaigns.stop.body',
    defaultMessage:
      'This will mark <b>"{name}"</b> as finished. No more automated runs will be scheduled.',
  },
  deleteModalTitle: {id: 'comms.campaigns.delete.title', defaultMessage: 'Delete Campaign'},
  clearModalTitle: {id: 'comms.campaigns.clear.title', defaultMessage: 'Clear All Targets'},
  clearModalConfirm: {
    id: 'comms.campaigns.clear.confirm',
    defaultMessage: 'Remove all targets?',
  },
  clearModalBody: {
    id: 'comms.campaigns.clear.body',
    defaultMessage:
      'This will remove all <b>{count}</b> targets from this campaign. This cannot be undone.',
  },
  clearModalAction: {
    id: 'comms.campaigns.clear.action',
    defaultMessage: 'Clear all targets',
  },
  removeTargetTitle: {
    id: 'comms.campaigns.removeTarget.title',
    defaultMessage: 'Remove Target',
  },
  removeTargetBody: {
    id: 'comms.campaigns.removeTarget.body',
    defaultMessage: 'Remove <b>{name}</b>? This cannot be undone.',
  },
  remove: {id: 'comms.campaigns.removeTarget.action', defaultMessage: 'Remove'},
  saveTargets: {id: 'comms.campaigns.target.save', defaultMessage: 'Save targets'},

  // ── Toasts ──
  toastCampaignUpdated: {
    id: 'comms.campaigns.toast.updated',
    defaultMessage: 'Campaign updated',
  },
  toastCampaignCreated: {
    id: 'comms.campaigns.toast.created',
    defaultMessage: 'Campaign created',
  },
  toastCampaignSaveError: {
    id: 'comms.campaigns.toast.saveError',
    defaultMessage: 'There was a problem saving the campaign',
  },
  toastTemplateUpdated: {
    id: 'comms.campaigns.toast.templateUpdated',
    defaultMessage: 'Template updated',
  },
  toastTemplateSaveError: {
    id: 'comms.campaigns.toast.templateSaveError',
    defaultMessage: 'Something went wrong saving the template',
  },
  toastTargetsAdded: {
    id: 'comms.campaigns.toast.targetsAdded',
    defaultMessage: '{count, plural, one {Target added} other {# targets added}}',
  },
  toastAddTargetsError: {
    id: 'comms.campaigns.toast.addTargetsError',
    defaultMessage: 'Unable to add targets',
  },
  toastTargetsUpdated: {
    id: 'comms.campaigns.toast.targetsUpdated',
    defaultMessage: 'Targets updated: {summary}',
  },
  toastTargetsAddedPart: {
    id: 'comms.campaigns.toast.addedPart',
    defaultMessage: '{count} added',
  },
  toastTargetsRemovedPart: {
    id: 'comms.campaigns.toast.removedPart',
    defaultMessage: '{count} removed',
  },
  toastNoChanges: {id: 'comms.campaigns.toast.noChanges', defaultMessage: 'No changes'},
  toastSaveTargetsError: {
    id: 'comms.campaigns.toast.saveTargetsError',
    defaultMessage: 'Unable to save targets',
  },
  toastQueued: {
    id: 'comms.campaigns.toast.queued',
    defaultMessage: 'Campaign queued for delivery',
  },
  toastLaunchError: {
    id: 'comms.campaigns.toast.launchError',
    defaultMessage: 'Unable to launch campaign',
  },
  toastPaused: {id: 'comms.campaigns.toast.paused', defaultMessage: 'Campaign paused'},
  toastPauseError: {
    id: 'comms.campaigns.toast.pauseError',
    defaultMessage: 'Unable to pause the campaign',
  },
  toastApprovalSent: {
    id: 'comms.campaigns.toast.approvalSent',
    defaultMessage:
      'Approval request sent to {count, plural, one {# supervisor} other {# supervisors}}',
  },
  toastApprovalError: {
    id: 'comms.campaigns.toast.approvalError',
    defaultMessage: 'Could not send approval request',
  },
  toastRecurringStopped: {
    id: 'comms.campaigns.toast.recurringStopped',
    defaultMessage: 'Recurring campaign stopped',
  },
  toastStopError: {
    id: 'comms.campaigns.toast.stopError',
    defaultMessage: 'Unable to stop campaign',
  },
  toastCampaignDeleted: {
    id: 'comms.campaigns.toast.deleted',
    defaultMessage: 'Campaign deleted',
  },
  toastDeleteError: {
    id: 'comms.campaigns.toast.deleteError',
    defaultMessage: 'Could not delete campaign',
  },
  toastTargetRemoved: {
    id: 'comms.campaigns.toast.targetRemoved',
    defaultMessage: 'Target removed',
  },
  toastTargetRemovedOk: {
    id: 'comms.campaigns.toast.targetRemovedOk',
    defaultMessage: 'Target removed successfully',
  },
  toastRemoveTargetError: {
    id: 'comms.campaigns.toast.removeTargetError',
    defaultMessage: 'Could not remove target',
  },
  toastRemoveTargetFailed: {
    id: 'comms.campaigns.toast.removeTargetFailed',
    defaultMessage: 'Error removing target',
  },
  toastTargetsRemoved: {
    id: 'comms.campaigns.toast.targetsRemoved',
    defaultMessage: '{count, plural, one {# target} other {# targets}} removed',
  },
  toastClearTargetsError: {
    id: 'comms.campaigns.toast.clearTargetsError',
    defaultMessage: 'Could not clear targets',
  },
  toastSelectTemplate: {
    id: 'comms.campaigns.toast.selectTemplate',
    defaultMessage: 'Please select a template before sending tests',
  },
  toastTestSent: {id: 'comms.campaigns.toast.testSent', defaultMessage: 'Test message sent'},
  toastTestError: {
    id: 'comms.campaigns.toast.testError',
    defaultMessage: 'Unable to send test message',
  },

  // ── Filtros de la campaña (lo que no existe ya en policies/recipients) ──
  filterByTags: {
    id: 'comms.campaigns.filters.tagsPlaceholder',
    defaultMessage: 'Filter by tags...',
  },
  effectiveYear: {
    id: 'comms.campaigns.filters.effectiveYear',
    defaultMessage: 'Effective Year',
  },

  // ── Selector de destinatarios ──
  selectorTitle: {
    id: 'comms.campaigns.selector.title',
    defaultMessage: 'Select Campaign Recipients',
  },
  selectorSearch: {
    id: 'comms.campaigns.selector.search',
    defaultMessage: 'Search contacts…',
  },
  importCsv: {
    id: 'comms.campaigns.selector.importCsv',
    defaultMessage: 'Import contacts from CSV',
  },
  availableContacts: {
    id: 'comms.campaigns.selector.available',
    defaultMessage: 'Available Contacts',
  },
  availableContactsCount: {
    id: 'comms.campaigns.selector.availableCount',
    defaultMessage: 'Available Contacts ({count})',
  },
  selectedRecipients: {
    id: 'comms.campaigns.selector.selected',
    defaultMessage: 'Selected Recipients',
  },
  noContactsFound: {
    id: 'comms.campaigns.selector.noContacts',
    defaultMessage: 'No contacts found',
  },
  searching: {id: 'comms.campaigns.selector.searching', defaultMessage: 'Searching...'},
  hideList: {id: 'comms.campaigns.selector.hideList', defaultMessage: 'Hide list'},
  showList: {id: 'comms.campaigns.selector.showList', defaultMessage: 'Show list'},
  hide: {id: 'comms.campaigns.selector.hide', defaultMessage: 'Hide'},
  show: {id: 'comms.campaigns.selector.show', defaultMessage: 'Show'},
  addSelected: {id: 'comms.campaigns.selector.addSelected', defaultMessage: 'Add selected'},
  addAllVisible: {id: 'comms.campaigns.selector.addAllVisible', defaultMessage: 'Add all visible'},
  removeSelected: {
    id: 'comms.campaigns.selector.removeSelected',
    defaultMessage: 'Remove selected',
  },
  removeAll: {id: 'comms.campaigns.selector.removeAll', defaultMessage: 'Remove all'},
  addAllMatching: {
    id: 'comms.campaigns.selector.addAllMatching',
    defaultMessage: 'Add all matching',
  },
  addAllMatchingHint: {
    id: 'comms.campaigns.selector.addAllMatchingHint',
    defaultMessage: 'Add all contacts matching current search and filters directly to the campaign',
  },
  removeAllMatching: {
    id: 'comms.campaigns.selector.removeAllMatching',
    defaultMessage: 'Remove all matching',
  },
  removeAllMatchingHint: {
    id: 'comms.campaigns.selector.removeAllMatchingHint',
    defaultMessage: 'Remove from selection all contacts matching current search and filters',
  },
  saveNeedsIds: {
    id: 'comms.campaigns.selector.saveNeedsIds',
    defaultMessage: 'Save (click Show to load)',
  },
  saveNeedsIdsHint: {
    id: 'comms.campaigns.selector.saveNeedsIdsHint',
    defaultMessage: 'Click "Show" on Selected Recipients to load IDs before saving',
  },
  saveSelection: {
    id: 'comms.campaigns.selector.saveSelection',
    defaultMessage: 'Save Selection ({count})',
  },
  contactSearchPlaceholder: {
    id: 'comms.campaigns.selector.contactSearch',
    defaultMessage: 'Search contact...',
  },
  contactSelectorSearch: {
    id: 'comms.campaigns.selector.contactSelectorSearch',
    defaultMessage: 'Search contacts by name, email, or phone...',
  },
  matchingCurrentFilters: {
    id: 'comms.campaigns.selector.matchingFilters',
    defaultMessage: '({count} matching current filters)',
  },
  noSelectedMatch: {
    id: 'comms.campaigns.selector.noSelectedMatch',
    defaultMessage: 'No selected recipients match current filters',
  },
  recipientsSelected: {
    id: 'comms.campaigns.selector.recipientsSelected',
    defaultMessage: '{count, plural, one {# recipient selected} other {# recipients selected}}',
  },
  adjustFilters: {
    id: 'comms.campaigns.selector.adjustFilters',
    defaultMessage: 'Adjust search or filters to see them',
  },
  detailsLoading: {
    id: 'comms.campaigns.selector.detailsLoading',
    defaultMessage: '(Details loading)',
  },
  noRecipientsYet: {
    id: 'comms.campaigns.selector.noRecipientsYet',
    defaultMessage: 'No recipients selected yet',
  },
  selectFromLeft: {
    id: 'comms.campaigns.selector.selectFromLeft',
    defaultMessage: 'Select contacts from the left',
  },
  toastAddedAsTargets: {
    id: 'comms.campaigns.toast.addedAsTargets',
    defaultMessage: '{count, plural, one {# contact} other {# contacts}} added as targets',
  },
  toastAlreadyExisted: {
    id: 'comms.campaigns.toast.alreadyExisted',
    defaultMessage: '({count} already existed)',
  },
  toastAddAllError: {
    id: 'comms.campaigns.toast.addAllError',
    defaultMessage: 'Could not add all matching contacts',
  },
  toastRemoveAllError: {
    id: 'comms.campaigns.toast.removeAllError',
    defaultMessage: 'Could not remove matching contacts',
  },

  // ── Importación CSV ──
  importTitle: {id: 'comms.campaigns.import.title', defaultMessage: 'Import Contacts from CSV'},
  importDropzone: {
    id: 'comms.campaigns.import.dropzone',
    defaultMessage: 'Drag and drop CSV file here',
  },
  importOr: {id: 'comms.campaigns.import.or', defaultMessage: 'or'},
  importChooseFile: {id: 'comms.campaigns.import.chooseFile', defaultMessage: 'Choose File'},
  importFormatTitle: {
    id: 'comms.campaigns.import.formatTitle',
    defaultMessage: 'CSV Format Requirements:',
  },
  importFormatColumns: {
    id: 'comms.campaigns.import.formatColumns',
    defaultMessage: 'Columns: {columns}',
  },
  importFormatEither: {
    id: 'comms.campaigns.import.formatEither',
    defaultMessage: 'Either email or phone is required',
  },
  importFormatHeaders: {
    id: 'comms.campaigns.import.formatHeaders',
    defaultMessage: 'First row should contain column headers',
  },
  importFormatExample: {
    id: 'comms.campaigns.import.formatExample',
    defaultMessage: 'Example: {example}',
  },
  importDownloadTemplate: {
    id: 'comms.campaigns.import.downloadTemplate',
    defaultMessage: 'Download Template',
  },
  importPreviewTitle: {id: 'comms.campaigns.import.previewTitle', defaultMessage: 'Preview Data'},
  importValidCount: {id: 'comms.campaigns.import.validCount', defaultMessage: '{count} Valid'},
  importErrorCount: {id: 'comms.campaigns.import.errorCount', defaultMessage: '{count} Errors'},
  importTotalCount: {id: 'comms.campaigns.import.totalCount', defaultMessage: '{count} Total'},
  importValid: {id: 'comms.campaigns.import.valid', defaultMessage: 'Valid'},
  importShowingFirst: {
    id: 'comms.campaigns.import.showingFirst',
    defaultMessage: 'Showing first {shown} of {total} rows',
  },
  importInProgress: {
    id: 'comms.campaigns.import.inProgress',
    defaultMessage: 'Importing Contacts...',
  },
  importPleaseWait: {
    id: 'comms.campaigns.import.pleaseWait',
    defaultMessage: 'Please wait while we create your contacts',
  },
  importSuccessTitle: {
    id: 'comms.campaigns.import.successTitle',
    defaultMessage: 'Import Successful!',
  },
  importProcessed: {
    id: 'comms.campaigns.import.processed',
    defaultMessage: '{count, plural, one {# contact} other {# contacts}} processed successfully.',
  },
  importNewCreated: {id: 'comms.campaigns.import.newCreated', defaultMessage: 'New Created'},
  importExistingTagged: {
    id: 'comms.campaigns.import.existingTagged',
    defaultMessage: 'Existing Tagged',
  },
  importAction: {
    id: 'comms.campaigns.import.action',
    defaultMessage: 'Import {count, plural, one {# Contact} other {# Contacts}}',
  },
  importDone: {id: 'comms.campaigns.import.done', defaultMessage: 'Done'},
  importAlertNotCsv: {
    id: 'comms.campaigns.import.alertNotCsv',
    defaultMessage: 'Please upload a CSV file',
  },
  importAlertParse: {
    id: 'comms.campaigns.import.alertParse',
    defaultMessage: 'Error parsing CSV: {message}',
  },
  importAlertNoValid: {
    id: 'comms.campaigns.import.alertNoValid',
    defaultMessage: 'No valid contacts to import.',
  },
  importAlertFailed: {
    id: 'comms.campaigns.import.alertFailed',
    defaultMessage: 'Failed to import contacts. Please check your data.',
  },
  importAlertError: {
    id: 'comms.campaigns.import.alertError',
    defaultMessage: 'Error importing contacts: {message}',
  },
  unknownError: {id: 'comms.campaigns.import.unknownError', defaultMessage: 'Unknown error'},

  // ── Gestor de medios ──
  mediaTitle: {id: 'comms.campaigns.media.title', defaultMessage: 'Campaign Assets'},
  mediaSubtitle: {
    id: 'comms.campaigns.media.subtitle',
    defaultMessage: 'Manage public images and media for your campaigns.',
  },
  mediaUploadNew: {id: 'comms.campaigns.media.uploadNew', defaultMessage: 'Upload New Media'},
  mediaSearch: {
    id: 'comms.campaigns.media.search',
    defaultMessage: 'Search media by name...',
  },
  mediaShowing: {
    id: 'comms.campaigns.media.showing',
    defaultMessage: '{count, plural, one {Showing # asset} other {Showing # assets}}',
  },
  mediaLoading: {id: 'comms.campaigns.media.loading', defaultMessage: 'Loading assets...'},
  mediaEmpty: {id: 'comms.campaigns.media.empty', defaultMessage: 'No media assets found'},
  mediaEmptyHint: {
    id: 'comms.campaigns.media.emptyHint',
    defaultMessage: 'Upload images to use them in your campaigns.',
  },
  mediaUploadNow: {id: 'comms.campaigns.media.uploadNow', defaultMessage: 'Upload Now'},
  mediaCopyLink: {id: 'comms.campaigns.media.copyLink', defaultMessage: 'Copy Public Link'},
  mediaIdLabel: {id: 'comms.campaigns.media.idLabel', defaultMessage: 'ID: {id}'},
  mediaDeleteTitle: {id: 'comms.campaigns.media.deleteTitle', defaultMessage: 'Delete Image'},
  mediaDeleteBody: {
    id: 'comms.campaigns.media.deleteBody',
    defaultMessage: 'Are you sure you want to delete "<b>{name}</b>"?',
  },
  mediaDeleteHint: {
    id: 'comms.campaigns.media.deleteHint',
    defaultMessage: 'This action cannot be undone and may affect campaigns using this image.',
  },
  toastLinkCopied: {
    id: 'comms.campaigns.toast.linkCopied',
    defaultMessage: 'Link copied to clipboard!',
  },
  toastImageDeleted: {
    id: 'comms.campaigns.toast.imageDeleted',
    defaultMessage: 'Image deleted successfully',
  },
  toastImageDeleteError: {
    id: 'comms.campaigns.toast.imageDeleteError',
    defaultMessage: 'Error deleting image',
  },

  // ── Modal de subida de medios ──
  mediaModalTitle: {
    id: 'comms.campaigns.mediaUpload.title',
    defaultMessage: 'Upload Campaign Media',
  },
  mediaFile: {id: 'comms.campaigns.mediaUpload.file', defaultMessage: 'File'},
  mediaNamePlaceholder: {
    id: 'comms.campaigns.mediaUpload.namePlaceholder',
    defaultMessage: 'e.g. Summer Promo Header',
  },
  mediaVisible: {
    id: 'comms.campaigns.mediaUpload.visible',
    defaultMessage: 'Visible for others (Public)',
  },
  mediaVisibleHint: {
    id: 'comms.campaigns.mediaUpload.visibleHint',
    defaultMessage: 'Check this to ensure the file can be viewed by email recipients.',
  },
  mediaUploadAction: {id: 'comms.campaigns.mediaUpload.action', defaultMessage: 'Upload'},
  toastUploadFailed: {
    id: 'comms.campaigns.toast.uploadFailed',
    defaultMessage: 'Upload failed',
  },
  toastMediaUploaded: {
    id: 'comms.campaigns.toast.mediaUploaded',
    defaultMessage: 'Media uploaded successfully!',
  },
  validationFileRequired: {
    id: 'validation.file.required',
    defaultMessage: 'File is required',
  },
  validationNameRequired: {
    id: 'validation.name.required',
    defaultMessage: 'Name is required',
  },

  // ── Importar desde archivo a PolicyDial ──
  fileFieldIgnore: {id: 'comms.campaigns.file.ignore', defaultMessage: '— Ignore —'},
  fileFieldPhone: {id: 'comms.campaigns.file.phone', defaultMessage: 'Phone (required)'},
  fileFieldFullName: {id: 'comms.campaigns.file.fullName', defaultMessage: 'Full name (split)'},
  fileFieldPostalCode: {
    id: 'comms.campaigns.file.postalCode',
    defaultMessage: 'ZIP / Postal code',
  },
  fileFieldComments: {
    id: 'comms.campaigns.file.comments',
    defaultMessage: 'Comments / Notes',
  },
  fileParseError: {
    id: 'comms.campaigns.file.parseError',
    defaultMessage: 'Failed to parse file: {message}',
  },
  fileReadError: {
    id: 'comms.campaigns.file.readError',
    defaultMessage: 'Failed to read file',
  },
  fileStep1: {
    id: 'comms.campaigns.file.step1',
    defaultMessage: 'Step 1: Select a CSV or Excel file',
  },
  fileStep2: {id: 'comms.campaigns.file.step2', defaultMessage: 'Step 2: Map your columns'},
  fileClear: {id: 'comms.campaigns.file.clear', defaultMessage: 'Clear'},
  fileTemplateHint: {
    id: 'comms.campaigns.file.templateHint',
    defaultMessage:
      'Use <b>Download leads template</b> (top right of the page) for a pre-filled Excel with the column names we auto-recognize ({phoneField} required).',
  },
  filePreviewLabel: {
    id: 'comms.campaigns.file.previewLabel',
    defaultMessage: 'Preview ({total} rows in file{suffix})',
  },
  filePreviewSuffix: {
    id: 'comms.campaigns.file.previewSuffix',
    defaultMessage: ', showing first {shown}',
  },
  filePhoneRequired: {
    id: 'comms.campaigns.file.phoneRequired',
    defaultMessage: 'Phone column is required. Map one column to <b>Phone</b> to continue.',
  },
  fileTooManyRows: {
    id: 'comms.campaigns.file.tooManyRows',
    defaultMessage: 'Too many rows: {rows}. Maximum is {max} per upload.',
  },
  fileReadyToPush: {
    id: 'comms.campaigns.file.readyToPush',
    defaultMessage:
      'Ready to push <b>{ready}</b> of {total} rows to <b>{campaign}</b>. Sent in batches of {chunk} — keep this tab open until it finishes. Duplicates by phone are removed within the file.',
  },
  filePushing: {id: 'comms.campaigns.file.pushing', defaultMessage: 'Pushing {progress}'},
  filePush: {id: 'comms.campaigns.file.push', defaultMessage: 'Push {count} to {dialer}'},
  fileUploadingWarning: {
    id: 'comms.campaigns.file.uploadingWarning',
    defaultMessage: 'Uploading {count} leads — do NOT close this tab or switch tabs',
  },
  filePushingChunk: {
    id: 'comms.campaigns.file.pushingChunk',
    defaultMessage:
      'Pushing chunk {current} of {total}. Closing the tab now will abort the remaining leads.',
  },
  fileIssues: {id: 'comms.campaigns.file.issues', defaultMessage: 'Issues:'},
  fileColRow: {id: 'comms.campaigns.file.colRow', defaultMessage: 'Row'},
  fileColReason: {id: 'comms.campaigns.file.colReason', defaultMessage: 'Reason'},
  fileStatAdded: {id: 'comms.campaigns.file.statAdded', defaultMessage: 'Added'},
  fileStatSkipped: {id: 'comms.campaigns.file.statSkipped', defaultMessage: 'Skipped'},
  fileImportAnother: {
    id: 'comms.campaigns.file.importAnother',
    defaultMessage: 'Import another file',
  },
  toastSelectList: {
    id: 'comms.campaigns.toast.selectList',
    defaultMessage: 'Select a target list for this campaign at the top of the page',
  },
  toastChunkFailed: {
    id: 'comms.campaigns.toast.chunkFailed',
    defaultMessage: 'Chunk {from}-{to} failed: {message}',
  },
  toastLeadsAdded: {
    id: 'comms.campaigns.toast.leadsAdded',
    defaultMessage: '{count, plural, one {# lead} other {# leads}} added to {dialer}',
  },
  toastLeadsFailed: {
    id: 'comms.campaigns.toast.leadsFailed',
    defaultMessage: '{count, plural, one {# lead} other {# leads}} failed',
  },
  toastLeadsSkipped: {
    id: 'comms.campaigns.toast.leadsSkipped',
    defaultMessage: '{count, plural, one {# lead} other {# leads}} skipped',
  },
  requestFailed: {
    id: 'comms.campaigns.file.requestFailed',
    defaultMessage: 'Request failed',
  },
  firstName: {id: 'common.field.firstName', defaultMessage: 'First Name'},
  lastName: {id: 'common.field.lastName', defaultMessage: 'Last Name'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},

  // ── Panel de plantillas ──
  templatesSubtitle: {
    id: 'comms.campaigns.templates.subtitle',
    defaultMessage: 'Central repository of message templates for every channel.',
  },
  templatesCreate: {
    id: 'comms.campaigns.templates.create',
    defaultMessage: 'Create template',
  },
  templatesSearch: {
    id: 'comms.campaigns.templates.search',
    defaultMessage: 'Search by name or content',
  },
  templatesBlueprintCount: {
    id: 'comms.campaigns.templates.blueprintCount',
    defaultMessage: '{count, plural, one {# blueprint} other {# blueprints}}',
  },
  templatesFilterAll: {
    id: 'comms.campaigns.templates.filterAll',
    defaultMessage: 'All templates',
  },
  templatesFilterBlueprints: {
    id: 'comms.campaigns.templates.filterBlueprints',
    defaultMessage: 'Blueprints only',
  },
  templatesFilterRegular: {
    id: 'comms.campaigns.templates.filterRegular',
    defaultMessage: 'Regular only',
  },
  templatesAllUsers: {id: 'comms.campaigns.templates.allUsers', defaultMessage: 'All users'},
  templatesAllOrgs: {
    id: 'comms.campaigns.templates.allOrgs',
    defaultMessage: 'All organizations',
  },
  templatesEmpty: {
    id: 'comms.campaigns.templates.empty',
    defaultMessage: 'No templates yet. Start by creating one.',
  },
  templatesNoMatch: {
    id: 'comms.campaigns.templates.noMatch',
    defaultMessage: 'No templates match the selected filters.',
  },
  templatesColVariables: {
    id: 'comms.campaigns.templates.colVariables',
    defaultMessage: 'Variables',
  },
  templatesColCreatedBy: {
    id: 'comms.campaigns.templates.colCreatedBy',
    defaultMessage: 'Created by',
  },
  templatesColUpdated: {id: 'comms.campaigns.templates.colUpdated', defaultMessage: 'Updated'},
  templatesBlueprint: {
    id: 'comms.campaigns.templates.blueprint',
    defaultMessage: 'Blueprint',
  },
  templatesBlueprintTooltip: {
    id: 'comms.campaigns.templates.blueprintTooltip',
    defaultMessage: 'Blueprint template',
  },
  templatesUnmarkBlueprint: {
    id: 'comms.campaigns.templates.unmarkBlueprint',
    defaultMessage: 'Remove blueprint status',
  },
  templatesMarkBlueprint: {
    id: 'comms.campaigns.templates.markBlueprint',
    defaultMessage: 'Mark as blueprint',
  },
  templatesDuplicate: {id: 'comms.campaigns.templates.duplicate', defaultMessage: 'Duplicate'},
  templatesViewBlueprint: {
    id: 'comms.campaigns.templates.viewBlueprint',
    defaultMessage: 'View Blueprint',
  },
  templatesVariants: {id: 'comms.campaigns.templates.variants', defaultMessage: 'Variants'},

  // ── Formulario de plantilla ──
  templateFormDuplicate: {
    id: 'comms.campaigns.templateForm.duplicate',
    defaultMessage: 'Duplicate template',
  },
  templateFormEdit: {
    id: 'comms.campaigns.templateForm.edit',
    defaultMessage: 'Edit template',
  },
  templateFormCreate: {
    id: 'comms.campaigns.templateForm.create',
    defaultMessage: 'Create template',
  },
  templateFormSelectOrg: {
    id: 'comms.campaigns.templateForm.selectOrg',
    defaultMessage: '— Select organization —',
  },
  templateFormNamePlaceholder: {
    id: 'comms.campaigns.templateForm.namePlaceholder',
    defaultMessage: 'e.g. Renewal Reminder',
  },
  templateFormEnabledChannels: {
    id: 'comms.campaigns.templateForm.enabledChannels',
    defaultMessage: 'Enabled Channels',
  },
  templateFormChannelsHint: {
    id: 'comms.campaigns.templateForm.channelsHint',
    defaultMessage: 'Select which channels you want to configure for this template.',
  },
  templateFormSubjectPlaceholder: {
    id: 'comms.campaigns.templateForm.subjectPlaceholder',
    defaultMessage: 'e.g. Important notice about your policy',
  },
  templateFormSubjectHint: {
    id: 'comms.campaigns.templateForm.subjectHint',
    defaultMessage: 'Default subject for email variants.',
  },
  templateFormCreateAction: {
    id: 'comms.campaigns.templateForm.createAction',
    defaultMessage: 'Create',
  },

  // ── PolicyDial ──
  dialerTitle: {
    id: 'comms.campaigns.dialer.title',
    defaultMessage: '{dialer} Dialer Campaigns',
  },
  dialerSubtitle: {
    id: 'comms.campaigns.dialer.subtitle',
    defaultMessage: 'Push leads from {product} to {dialer} auto-dialer campaigns.',
  },
  dialerDownloadTemplate: {
    id: 'comms.campaigns.dialer.downloadTemplate',
    defaultMessage: 'Download leads template',
  },
  dialerNoCampaigns: {
    id: 'comms.campaigns.dialer.noCampaigns',
    defaultMessage: 'No campaigns found',
  },
  dialerActive: {id: 'comms.campaigns.dialer.active', defaultMessage: 'Active'},
  dialerOff: {id: 'comms.campaigns.dialer.off', defaultMessage: 'Off'},
  dialerSelectCampaign: {
    id: 'comms.campaigns.dialer.selectCampaign',
    defaultMessage: 'Select a {dialer} campaign to add leads',
  },
  dialerTargetList: {id: 'comms.campaigns.dialer.targetList', defaultMessage: 'Target list'},
  dialerLoadingLists: {
    id: 'comms.campaigns.dialer.loadingLists',
    defaultMessage: 'Loading lists...',
  },
  dialerNoLists: {
    id: 'comms.campaigns.dialer.noLists',
    defaultMessage: 'No active lists for this campaign',
  },
  dialerSelectList: {
    id: 'comms.campaigns.dialer.selectList',
    defaultMessage: '— Select a list —',
  },
  dialerFromProduct: {
    id: 'comms.campaigns.dialer.fromProduct',
    defaultMessage: 'From {product}',
  },
  dialerImportFromFile: {
    id: 'comms.campaigns.dialer.importFromFile',
    defaultMessage: 'Import from file',
  },
  dialerPushFiltered: {
    id: 'comms.campaigns.dialer.pushFiltered',
    defaultMessage: 'Push All {count} Filtered',
  },
  dialerPushSelected: {
    id: 'comms.campaigns.dialer.pushSelected',
    defaultMessage: 'Push {count} Selected',
  },
  dialerPipeline: {id: 'comms.campaigns.dialer.pipeline', defaultMessage: 'Pipeline'},
  dialerAllPipelines: {
    id: 'comms.campaigns.dialer.allPipelines',
    defaultMessage: 'All Pipelines',
  },
  dialerStage: {id: 'comms.campaigns.dialer.stage', defaultMessage: 'Stage'},
  dialerAllStages: {id: 'comms.campaigns.dialer.allStages', defaultMessage: 'All Stages'},
  dialerFrom: {id: 'comms.campaigns.dialer.from', defaultMessage: 'From'},
  dialerTo: {id: 'comms.campaigns.dialer.to', defaultMessage: 'To'},
  dialerLeadNamePlaceholder: {
    id: 'comms.campaigns.dialer.leadNamePlaceholder',
    defaultMessage: 'Lead name...',
  },
  dialerPhonePlaceholder: {
    id: 'comms.campaigns.dialer.phonePlaceholder',
    defaultMessage: 'Phone number...',
  },
  dialerSearching: {id: 'comms.campaigns.dialer.searching', defaultMessage: 'Searching...'},
  dialerLeadsFound: {
    id: 'comms.campaigns.dialer.leadsFound',
    defaultMessage: '{count} leads found',
  },
  dialerSelectedCount: {
    id: 'comms.campaigns.dialer.selectedCount',
    defaultMessage: '{count} selected',
  },
  dialerNoLeads: {
    id: 'comms.campaigns.dialer.noLeads',
    defaultMessage: 'No leads match your filters',
  },
  dialerPushResults: {
    id: 'comms.campaigns.dialer.pushResults',
    defaultMessage: 'Push Results',
  },
  dialerConfirmPush: {
    id: 'comms.campaigns.dialer.confirmPush',
    defaultMessage: 'Confirm Push to {dialer}',
  },
  dialerPushLeadsTo: {
    id: 'comms.campaigns.dialer.pushLeadsTo',
    defaultMessage: 'Push leads to <b>{campaign}</b>:',
  },
  dialerHopperHint: {
    id: 'comms.campaigns.dialer.hopperHint',
    defaultMessage:
      "Leads will be added to {dialer}'s hopper and dialed automatically. A tag {tag} will be assigned to each lead.",
  },
  formEditTitle: {id: 'comms.campaigns.form.editTitle', defaultMessage: 'Edit campaign'},
  formCreateTitle: {id: 'comms.campaigns.form.createTitle', defaultMessage: 'Create campaign'},
  formNamePlaceholder: {
    id: 'comms.campaigns.form.namePlaceholder',
    defaultMessage: 'My new campaign',
  },
  formDescriptionPlaceholder: {
    id: 'comms.campaigns.form.descriptionPlaceholder',
    defaultMessage: 'Campaign goal or internal notes',
  },
  formSelectTemplate: {
    id: 'comms.campaigns.form.selectTemplate',
    defaultMessage: 'Select a template',
  },
  formChannelsStrategy: {
    id: 'comms.campaigns.form.channelsStrategy',
    defaultMessage: 'Channels & Strategy',
  },
  formStrategyWaterfall: {
    id: 'comms.campaigns.form.strategyWaterfall',
    defaultMessage: 'Smart Priority',
  },
  formStrategyBroadcast: {
    id: 'comms.campaigns.form.strategyBroadcast',
    defaultMessage: 'Broadcast to All',
  },
  formWaterfallHint: {
    id: 'comms.campaigns.form.waterfallHint',
    defaultMessage:
      'System will try channels in the order below. If a message is sent successfully, subsequent channels will be skipped.',
  },
  formNotInTemplate: {
    id: 'comms.campaigns.form.notInTemplate',
    defaultMessage: 'Not in template',
  },
  formSelectTemplateFirst: {
    id: 'comms.campaigns.form.selectTemplateFirst',
    defaultMessage: 'Select a template to add channels',
  },
  formScheduling: {id: 'comms.campaigns.form.scheduling', defaultMessage: 'Scheduling'},
  formImmediate: {id: 'comms.campaigns.form.immediate', defaultMessage: 'Immediate'},
  formScheduled: {id: 'comms.campaigns.form.scheduled', defaultMessage: 'Scheduled'},
  formRecurring: {id: 'comms.campaigns.form.recurring', defaultMessage: 'Recurring'},
  formNextExecution: {
    id: 'comms.campaigns.form.nextExecution',
    defaultMessage: 'Next execution',
  },
  formExecutionTime: {
    id: 'comms.campaigns.form.executionTime',
    defaultMessage: 'Execution time',
  },
  formRepeatEvery: {id: 'comms.campaigns.form.repeatEvery', defaultMessage: 'Repeat every'},
  formUnitHours: {id: 'comms.campaigns.form.unitHours', defaultMessage: 'Hours'},
  formUnitDays: {id: 'comms.campaigns.form.unitDays', defaultMessage: 'Days'},
  formUnitWeeks: {id: 'comms.campaigns.form.unitWeeks', defaultMessage: 'Weeks'},
  formUnitMonths: {id: 'comms.campaigns.form.unitMonths', defaultMessage: 'Months'},
  formNHours: {
    id: 'comms.campaigns.form.nHours',
    defaultMessage: '{count, plural, one {# hour} other {# hours}}',
  },
  formNDays: {
    id: 'comms.campaigns.form.nDays',
    defaultMessage: '{count, plural, one {# day} other {# days}}',
  },
  formNWeeks: {
    id: 'comms.campaigns.form.nWeeks',
    defaultMessage: '{count, plural, one {# week} other {# weeks}}',
  },
  formNMonths: {
    id: 'comms.campaigns.form.nMonths',
    defaultMessage: '{count, plural, one {# month} other {# months}}',
  },
  formOneYear: {id: 'comms.campaigns.form.oneYear', defaultMessage: '1 year'},
  formIntervalHint: {
    id: 'comms.campaigns.form.intervalHint',
    defaultMessage: 'Min. 12 hours · ~{days, plural, one {# day} other {# days}} between runs',
  },
  formStopCondition: {
    id: 'comms.campaigns.form.stopCondition',
    defaultMessage: 'Stop condition',
  },
  formStopNever: {
    id: 'comms.campaigns.form.stopNever',
    defaultMessage: 'Never (manual pause)',
  },
  formStopAfterRuns: {
    id: 'comms.campaigns.form.stopAfterRuns',
    defaultMessage: 'After N runs',
  },
  formStopOnDate: {
    id: 'comms.campaigns.form.stopOnDate',
    defaultMessage: 'On specific date',
  },
  formStopAfter: {id: 'comms.campaigns.form.stopAfter', defaultMessage: 'Stop after'},
  formExecutions: {id: 'comms.campaigns.form.executions', defaultMessage: 'executions'},
  formAutoCompletes: {
    id: 'comms.campaigns.form.autoCompletes',
    defaultMessage: 'Campaign auto-completes on this date',
  },
  dialerAddLeadsTo: {
    id: 'comms.campaigns.dialer.addLeadsTo',
    defaultMessage: 'Add Leads to:',
  },
  dialerWaitImport: {
    id: 'comms.campaigns.dialer.waitImport',
    defaultMessage: 'Wait for the file import to finish before switching tabs',
  },
  tags: {id: 'comms.tags.label', defaultMessage: 'Tags'},
  organization: {id: 'comms.field.organization', defaultMessage: 'Organization'},
  allChannels: {id: 'comms.field.allChannels', defaultMessage: 'All channels'},
  subject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  description: {id: 'common.field.description', defaultMessage: 'Description'},
  save: {id: 'common.action.save', defaultMessage: 'Save'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},

  // ── Vocabulario compartido del módulo `comms.*` ──
  sent: {id: 'comms.field.sent', defaultMessage: 'Sent'},
  channel: {id: 'comms.field.channel', defaultMessage: 'Channel'},
  channels: {id: 'comms.field.channels', defaultMessage: 'Channels'},
  template: {id: 'comms.field.template', defaultMessage: 'Template'},
  error: {id: 'comms.field.error', defaultMessage: 'Error'},

  // ── Reutilizadas de `common.*` ──
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  contact: {id: 'common.field.contact', defaultMessage: 'Contact'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  back: {id: 'common.action.back', defaultMessage: 'Back'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  view: {id: 'common.action.view', defaultMessage: 'View'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  areYouSure: {id: 'common.confirm.areYouSure', defaultMessage: 'Are you sure?'},
  deleteBody: {
    id: 'common.confirm.deleteBody',
    defaultMessage: 'You are about to delete <b>"{name}"</b>. This cannot be undone.',
  },
})

/** Símbolos sueltos: diseño, no texto (guía §17.3). */
export const EM_DASH = '—'
export const ICON_SEARCH = '🔍'
export const CSV_LABEL = 'CSV'
/** Marcas: no se traducen. */
export const DIALER_POLICYDIAL = 'PolicyDial'
export const DIALER_VICIDIAL = 'VICIdial'
/** Columnas y valor de ejemplo del CSV: son literales del formato, no texto. */
export const CSV_COLUMNS = 'first_name, last_name, email, phone'
export const CSV_EXAMPLE = 'John,Doe,john@example.com,+1234567890'
export const CSV_PHONE_FIELD = 'phone'
export const ROW_NUMBER_HEADER = '#'
export const REQUIRED_MARK = '*'
export const PRODUCT_POLICYTOOLS = 'PolicyTools'
/** Estrellas del blueprint y emoji de variantes: iconos, no texto. */
export const STAR_FILLED = '⭐'
export const STAR_EMPTY = '☆'
export const ICON_VARIANTS = '📝'
/** Prefijo de la etiqueta que PolicyDial asigna a cada lead. */
export const VD_TAG_PREFIX = 'VD:'

/** `101 — Lista principal` en el selector de listas de PolicyDial. */
export const listOption = (id: string | number, name: string) => `${id} — ${name}`
/** `VD: Campaña de verano` */
export const vdTag = (campaign?: string) => `${VD_TAG_PREFIX} ${campaign ?? ''}`.trim()

/** Pega un icono a un texto ya traducido sin que el rule marque el template literal. */
export const withIcon = (icon: string, label: string) => `${icon} ${label}`
export const ELLIPSIS = '…'

/** Ejemplos de correo en los placeholders de la prueba: no son texto traducible. */
export const EXAMPLE_FROM_EMAIL = 'news@company.com'
export const EXAMPLE_REPLY_TO = 'support@company.com'

/** `85%` — evita el template literal como hijo JSX (guía §17.4). */
export const formatPercent = (value: number) => `${value}%`

/** Nombre completo de un contacto cuando el backend no manda uno armado. */
export const fullName = (first?: string | null, last?: string | null) =>
  `${first ?? ''} ${last ?? ''}`.trim()
